import { Component, OnInit } from '@angular/core';
import { NavigationService } from '../navigation.service';
import { InregistrareProgramareService, DetaliiProgramareState } from '../inregistrare-programare.service';
import { Entitate } from '../inregistrare-programare.interface';
import { SubcontractLocal, SubcontractLocalSlot } from '../disponibilitate-programare/disponibilitate.interface';
import { finalize } from 'rxjs';
import { helper } from 'src/environments/helper';
import Swal from 'sweetalert2';
import { Contract, ContractFileGroup } from '../../contracte-programari/contracte-programari.interface';
import { ContracteProgramariService } from '../../contracte-programari/contracte-programari.service';
import { AppFilesService } from 'src/app/services/app-files.service';

@Component({
    selector: 'app-finalize-programare',
    templateUrl: './finalize-programare.component.html',
    styleUrls: ['./finalize-programare.component.scss']
})
export class FinalizeProgramareComponent implements OnInit {
    helper = helper;

    entitate: Entitate | null = null;
    detaliiState: DetaliiProgramareState | null = null;
    subcontracteLocale: SubcontractLocal[] = [];
    isLoading = false;

    // Contractul curent — folosit de app-documente
    currentContract: Contract | null = null;

    private readonly DOW_RO = ['Dum', 'Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm'];

    constructor(
        public navigationService: NavigationService,
        private inregistrareProgramareService: InregistrareProgramareService,
        private contracteProgramariService: ContracteProgramariService,
        private appFilesService: AppFilesService,
    ) { }

    ngOnInit(): void {
        this.navigationService.currentStepIndex = 2;
        this.entitate = this.inregistrareProgramareService.getSelectedEntitate();

        if (!this.entitate) {
            this.navigationService.goToStep(0);
            return;
        }

        this.detaliiState = this.inregistrareProgramareService.getDetaliiState();
        this.subcontracteLocale = this.detaliiState?.subcontracteLocale ?? [];

        // Construiește contractul din state — file_groups vine din draft (listContracte)
        // sau este array gol pentru un contract nou
        this.buildCurrentContract();
    }

    // ── Construiește obiectul Contract pentru app-documente ───────────────────
    // Nu face niciun call HTTP — folosește doar datele deja în state.

    private buildCurrentContract(): void {
        if (!this.detaliiState?.contractId) return;

        this.currentContract = {
            id: this.detaliiState.contractId,
            entity_id: this.entitate!.id,
            date_from_contract: this.detaliiState.selectedStartDate
                ? this.formatDateRender(this.detaliiState.selectedStartDate)
                : '',
            date_end_contract: this.detaliiState.selectedEndDate
                ? this.formatDateRender(this.detaliiState.selectedEndDate)
                : '',
            active: false,
            nr_contract: this.detaliiState.nrContract ?? null,
            data_contract: this.detaliiState.dataContract ?? null,
            subcontracts: [],
            entity: this.entitate,
            file_groups: this.detaliiState.fileGroups ?? [],
        } as unknown as Contract;
    }

    // ── Handler pentru (contractChanged) din app-documente ────────────────────
    // Apelat DOAR după add/edit document — face getContract pentru a refresh
    // file_groups cu datele actualizate de pe backend.

    onContractChanged(): void {
        if (!this.detaliiState?.contractId) return;

        this.isLoading = true;
        this.inregistrareProgramareService
            .getContract(this.detaliiState.contractId)
            .pipe(finalize(() => { this.isLoading = false; }))
            .subscribe({
                next: (res: any) => {
                    const fileGroups: ContractFileGroup[] = res.data?.file_groups ?? res.file_groups ?? [];

                    // Actualizăm doar file_groups, păstrăm restul datelor locale
                    if (this.currentContract) {
                        this.currentContract = {
                            ...this.currentContract,
                            file_groups: fileGroups,
                        };
                    }

                    // Persistăm în state pentru când userul navighează înapoi și revine
                    if (this.detaliiState) {
                        this.detaliiState.fileGroups = fileGroups;
                        this.inregistrareProgramareService.saveDetaliiState(this.detaliiState);
                    }
                },
                error: (err) => { console.error('Error refreshing contract:', err); }
            });
    }

    // ── Editare subcontract — întoarce la pasul 2 ─────────────────────────────

    onEditSubcontract(sub: SubcontractLocal): void {
        this.inregistrareProgramareService.setPendingEditSubcontractId(sub.subcontract_id);
        this.navigationService.goToStep(1);
    }

    // ── Ștergere subcontract ──────────────────────────────────────────────────

    onDeleteSubcontract(sub: SubcontractLocal): void {
        Swal.fire({
            icon: 'warning',
            title: 'Confirmare ștergere',
            text: `Ești sigur că vrei să ștergi programarea pentru "${sub.specializare_name}"?`,
            showCancelButton: true,
            confirmButtonText: 'Da, șterge',
            cancelButtonText: 'Anulează',
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#9e9e9e',
            reverseButtons: true,
        }).then(result => {
            if (!result.isConfirmed) return;

            this.isLoading = true;
            this.inregistrareProgramareService.deleteSubcontract(sub.subcontract_id).pipe(
                finalize(() => { this.isLoading = false; })
            ).subscribe({
                next: () => {
                    this.subcontracteLocale = this.subcontracteLocale.filter(
                        s => s.subcontract_id !== sub.subcontract_id
                    );
                    if (this.detaliiState) {
                        this.detaliiState.subcontracteLocale = [...this.subcontracteLocale];
                        this.inregistrareProgramareService.saveDetaliiState(this.detaliiState);
                    }
                },
                error: (err) => { console.error('Error deleteSubcontract:', err); }
            });
        });
    }

    // ── Finalizare ────────────────────────────────────────────────────────────

    handleFinalizare = () => {
        const contractId = this.detaliiState?.contractId;
        if (!contractId) return;

        this.isLoading = true;
        this.inregistrareProgramareService.finalizareContact(contractId).pipe(
            finalize(() => { this.isLoading = false; })
        ).subscribe({
            next: () => {
                Swal.fire({
                    icon: 'success',
                    title: 'Programare finalizată',
                    text: 'Programarea a fost înregistrată cu succes.',
                    confirmButtonColor: '#1976d2',
                    confirmButtonText: 'OK',
                }).then(() => {
                    this.inregistrareProgramareService.resetAll();
                    this.navigationService.goToStep(0);
                });
            },
            error: (err) => {
                console.error('Error finalizare:', err);
                Swal.fire({
                    icon: 'error',
                    title: 'Eroare',
                    text: 'A apărut o eroare la finalizarea programării. Vă rugăm încercați din nou.',
                    confirmButtonColor: '#1976d2',
                });
            }
        });
    }

    // ── Anulare contract ──────────────────────────────────────────────────────

    handleAnulare(): void {
        const contractId = this.detaliiState?.contractId;
        if (!contractId) return;

        Swal.fire({
            icon: 'warning', title: 'Anulare contract',
            text: 'Ești sigur că vrei să anulezi? Contractul și toate programările vor fi șterse.',
            showCancelButton: true,
            confirmButtonText: 'Da, anulează', cancelButtonText: 'Înapoi',
            confirmButtonColor: '#d32f2f', cancelButtonColor: '#9e9e9e',
            reverseButtons: true,
        }).then(result => {
            if (!result.isConfirmed) return;
            this.isLoading = true;
            this.inregistrareProgramareService.deleteContract(contractId).pipe(
                finalize(() => { this.isLoading = false; })
            ).subscribe({
                next: () => {
                    this.inregistrareProgramareService.resetAll();
                    this.navigationService.goToStep(0);
                },
                error: (err) => { console.error('Error deleteContract:', err); }
            });
        });
    }

    // ── Helpers afișare subcontracte ──────────────────────────────────────────

    getSpecialistiDinSubcontract(sub: SubcontractLocal): { nume: string; slots: SubcontractLocalSlot[] }[] {
        const map = new Map<string, SubcontractLocalSlot[]>();
        for (const slot of sub.timeSlots) {
            if (!map.has(slot.nume_specialist)) map.set(slot.nume_specialist, []);
            map.get(slot.nume_specialist)!.push(slot);
        }
        return Array.from(map.entries()).map(([nume, slots]) => ({
            nume,
            slots: slots.sort((a, b) => a.date.localeCompare(b.date))
        }));
    }

    formatSlotTime(slot: SubcontractLocalSlot): string {
        return slot.time_from ? slot.time_from.substring(0, 5) : '';
    }

    getDayOfWeekRo(dateStr: string): string {
        const d = new Date(dateStr + 'T00:00:00');
        return this.DOW_RO[d.getDay()];
    }

    private formatDateRender(date: Date): string {
        const d = new Date(date);
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${month}-${day}`;
    }

    downloadFile(id: number) {
        this.isLoading = true;
        const fullPath = this.contracteProgramariService.printContractUrl + id;

        this.appFilesService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('Download doc error: ', err);
                this.isLoading = false;
            })
    }

    private saveState(): void {
        if (!this.detaliiState) return;
        this.detaliiState.subcontracteLocale = [...this.subcontracteLocale];
        this.inregistrareProgramareService.saveDetaliiState(this.detaliiState);
    }
}